import type { Customer, Site } from '../../domain/inventory';

export interface SiteSuggestion {
  site: Site;
  customerName: string;
  path: string;
}

export const SITE_SUGGESTION_LIMIT = 8;
export const SITE_SEARCH_MIN_LENGTH = 2;

export function sitePath(site: Pick<Site, 'id' | 'customerId'>): string {
  return `/customers/${encodeURIComponent(site.customerId)}/sites/${encodeURIComponent(site.id)}`;
}

// Lower ranks sort first: the site code typed in full, then a code or name
// that starts with the text, then a word in the name, then anything else
// (region, official site ID, customer). Ties sort by code.
function rank(site: Site, customerName: string, needle: string): number | null {
  const code = site.name.toLowerCase();
  const name = (site.displayName ?? '').toLowerCase();
  if (code === needle) return 0;
  if (code.startsWith(needle)) return 1;
  if (name.startsWith(needle)) return 2;
  if (name.split(/[\s,/()-]+/).some((word) => word.startsWith(needle))) return 3;
  const rest = `${code} ${name} ${site.region ?? ''} ${site.officialSiteId ?? ''} ${customerName}`;
  return rest.toLowerCase().includes(needle) ? 4 : null;
}

export function matchSites(
  query: string,
  sites: readonly Site[],
  customers: readonly Customer[],
  limit = SITE_SUGGESTION_LIMIT,
): SiteSuggestion[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < SITE_SEARCH_MIN_LENGTH) return [];
  const customerNames = new Map(
    customers.map((customer) => [customer.id, customer.displayName ?? customer.name]),
  );
  const matches: { site: Site; customerName: string; rank: number }[] = [];
  for (const site of sites) {
    const customerName = customerNames.get(site.customerId) ?? '';
    const siteRank = rank(site, customerName, needle);
    if (siteRank !== null) matches.push({ site, customerName, rank: siteRank });
  }
  return matches
    .sort((a, b) => a.rank - b.rank || a.site.name.localeCompare(b.site.name))
    .slice(0, limit)
    .map(({ site, customerName }) => ({ site, customerName, path: sitePath(site) }));
}
